import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faMagnifyingGlass,
  faArrowRight,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import CategoryTabs from "components/shared/global-search/CategoryTabs";
import GroupedResults from "components/shared/global-search/GroupedResults";
import FullListResults from "components/shared/global-search/FullListResults";
import { LANGUAGE, DIRECTION } from "services/general";
import useMobileSearchModal from "./useMobileSearchModal";

/**
 * MobileSearchModal — Full-screen responsive search view for mobile devices.
 */
const MobileSearchModal = ({
  isDark = false,
  isOpen = false,
  onClose,
  onSelectResult,
}) => {
  const {
    t,
    searchInputRef,
    searchQuery,
    setSearchQuery,
    activeCategories,
    results,
    loading,
    error,
    handleCategoryChange,
    handleViewAll,
    getCurrentResults,
    getCurrentCategory,
    showGroupedResults,
  } = useMobileSearchModal({ isOpen, onClose });

  if (!isOpen) return null;

  const modalContent = (
    <div
      className={`fixed inset-0 z-[999999] flex flex-col ${
        isDark ? "bg-chrome text-white" : "bg-surface text-heading"
      } animate-in fade-in zoom-in-95 duration-200`}
      style={{
        direction: DIRECTION,
        fontFamily:
          LANGUAGE === "ar" ? '"Expo-Arabic-Book", sans-serif' : "inherit",
      }}
    >
      {/* Top Search Bar */}
      <div
        className={`p-3.5 sm:p-4 border-b shrink-0 flex items-center gap-3 ${
          isDark ? "border-white/[0.08]" : "border-gray-100"
        }`}
      >
        <button
          type="button"
          onClick={onClose}
          className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors border-0 cursor-pointer shrink-0 ${
            isDark
              ? "bg-white/[0.08] hover:bg-white/[0.15] text-white"
              : "bg-gray-100 hover:bg-gray-200 text-heading"
          }`}
          aria-label="Back"
        >
          <FontAwesomeIcon
            icon={DIRECTION === "rtl" ? faArrowRight : faArrowLeft}
            className="text-sm"
          />
        </button>

        <div
          className={`flex-1 flex items-center gap-2 px-3 rounded-xl border ${
            isDark
              ? "bg-white/[0.06] border-white/[0.12] text-white"
              : "bg-[#F9FAFB] border-gray-200 text-heading"
          }`}
        >
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="text-gray-400 text-sm shrink-0"
          />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("search_placeholder") || "ابحث في النظام..."}
            className={`w-full h-11 bg-transparent border-0 focus:outline-none text-sm font-medium ${
              isDark
                ? "text-white placeholder:text-white/40"
                : "text-heading placeholder:text-gray-400"
            }`}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="p-1 text-gray-400 hover:text-gray-600 border-0 bg-transparent cursor-pointer"
            >
              <FontAwesomeIcon icon={faXmark} className="text-sm" />
            </button>
          )}
        </div>
      </div>

      {/* Category Filter Tabs */}
      <div
        className={`p-3 border-b shrink-0 ${
          isDark ? "border-white/[0.06]" : "border-gray-50"
        }`}
      >
        <CategoryTabs
          activeCategories={activeCategories}
          onCategoryChange={handleCategoryChange}
        />
      </div>

      {/* Search Results Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {loading ? (
          <div className="py-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="text-text-muted text-sm mt-3">
              {t("globalSearch.loading") || "جاري البحث..."}
            </p>
          </div>
        ) : error ? (
          <div className="py-12 text-center text-danger text-sm">{error}</div>
        ) : !searchQuery.trim() ? (
          <div className="py-16 text-center text-text-muted">
            <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-xl">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </div>
            <p className="text-sm font-medium">
              {t("globalSearch.startTyping") ||
                "اكتب للبحث في الوحدات، العقود، الفواتير، الصيانة..."}
            </p>
          </div>
        ) : showGroupedResults ? (
          <GroupedResults
            results={results}
            onViewAll={handleViewAll}
            onItemClick={onSelectResult}
            searchQuery={searchQuery}
          />
        ) : (
          <FullListResults
            items={getCurrentResults()}
            category={getCurrentCategory()}
            onItemClick={onSelectResult}
            searchQuery={searchQuery}
          />
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default MobileSearchModal;
